import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '../ProtectedRoute';
import AdminRoute from '../AdminRoute';
import { useAuthenticationContext } from '../../../../application';
import { AuthLayout, PublicLayout } from '../../../layouts';
import AuthenticationLoadingState from '../molecules/AuthenticationLoadingState';
import CGUGuard from '../CGUGuard';
import LoadingState from '../../../components/shared/molecules/LoadingState';

// Lazy load all pages for code splitting
const SignIn = lazy(() => import('../../../pages/SignIn'));
const Account = lazy(() => import('../../../pages/Account'));
const UserListPage = lazy(() => import('../../../pages/users/UserListPage'));
const OrganizationListPage = lazy(() => import('../../../pages/organizations/OrganizationListPage'));
const LocationListPage = lazy(() => import('../../../pages/locations/LocationListPage'));
const SensorListPage = lazy(() => import('../../../pages/sensors/SensorListPage'));
const AlertListPage = lazy(() => import('../../../pages/alerts/AlertListPage'));
const EventListPage = lazy(() => import('../../../pages/events/EventListPage'));
const DashboardPage = lazy(() => import('../../../pages/dashboard/DashboardPage'));
const LocationDashboardPage = lazy(() => import('../../../pages/dashboard/LocationDashboardPage'));
const DocumentSearchPage = lazy(() => import('../../../pages/documentSearch').then(m => ({ default: m.DocumentSearchPage })));
const CGUPage = lazy(() => import('../../../pages/legal/CGUPage'));
const CGUAcceptancePage = lazy(() => import('../../../pages/legal/CGUAcceptancePage'));

// Loading fallback component
const PageLoadingFallback = () => (
  <LoadingState message="Chargement..." padding={3} />
);

const AppRouter: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuthenticationContext();

  return (
    <Router>
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
        {/* Public routes */}
        <Route 
          path="/sign-in" 
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <PublicLayout>
                <SignIn />
              </PublicLayout>
            )
          } 
        />

        {/* CGU Acceptance route - requires authentication but no CGU acceptance yet */}
        <Route 
          path="/cgu-acceptance" 
          element={
            <ProtectedRoute>
              <PublicLayout>
                <CGUAcceptancePage />
              </PublicLayout>
            </ProtectedRoute>
          } 
        />
        
        {/* Protected routes */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <CGUGuard>
                <AuthLayout>
                  <DashboardPage />
                </AuthLayout>
              </CGUGuard>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/dashboard/locations/:locationId" 
          element={
            <ProtectedRoute>
              <CGUGuard>
                <AuthLayout>
                  <LocationDashboardPage />
                </AuthLayout>
              </CGUGuard>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/sensors" 
          element={
            <ProtectedRoute>
              <CGUGuard>
                <AuthLayout>
                  <SensorListPage />
                </AuthLayout>
              </CGUGuard>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/locations" 
          element={
            <ProtectedRoute>
              <CGUGuard>
                <AuthLayout>
                  <LocationListPage />
                </AuthLayout>
              </CGUGuard>
            </ProtectedRoute>
          } 
        />
        
        <Route
          path="/alerts"
          element={
            <ProtectedRoute disallowedRoles={['locations_user']}>
              <CGUGuard>
                <AuthLayout>
                  <AlertListPage />
                </AuthLayout>
              </CGUGuard>
            </ProtectedRoute>
          }
        />
        
        <Route 
          path="/events" 
          element={
            <ProtectedRoute>
              <CGUGuard>
                <AuthLayout>
                  <EventListPage />
                </AuthLayout>
              </CGUGuard>
            </ProtectedRoute>
          } 
        />
        
        <Route
          path="/users"
          element={
            <AdminRoute>
              <CGUGuard>
                <AuthLayout>
                  <UserListPage />
                </AuthLayout>
              </CGUGuard>
            </AdminRoute>
          }
        />

        <Route
          path="/organizations"
          element={
            <AdminRoute>
              <CGUGuard>
                <AuthLayout>
                  <OrganizationListPage />
                </AuthLayout>
              </CGUGuard>
            </AdminRoute>
          }
        />

        <Route
          path="/document-search"
          element={
            <AdminRoute>
              <CGUGuard>
                <AuthLayout>
                  <DocumentSearchPage />
                </AuthLayout>
              </CGUGuard>
            </AdminRoute>
          }
        />

        <Route
          path="/account" 
          element={
            <ProtectedRoute>
              <CGUGuard>
                <AuthLayout>
                  <Account />
                </AuthLayout>
              </CGUGuard>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/cgu" 
          element={
            <ProtectedRoute>
              <CGUGuard>
                <AuthLayout>
                  <CGUPage />
                </AuthLayout>
              </CGUGuard>
            </ProtectedRoute>
          } 
        />
        
        {/* Default redirect */}
        <Route 
          path="/" 
          element={
            isLoading ? (
              <AuthenticationLoadingState />
            ) : (
              <Navigate to={isAuthenticated ? "/dashboard" : "/sign-in"} replace />
            )
          } 
        />
        
        {/* Catch all route */}
        <Route 
          path="*" 
          element={
            isLoading ? (
              <AuthenticationLoadingState />
            ) : (
              <Navigate to={isAuthenticated ? "/dashboard" : "/sign-in"} replace />
            )
          }
        />
      </Routes>
      </Suspense>
    </Router>
  );
};

export default AppRouter;